import * as editorActions from '../actions/editor.action';
import * as elementsActions from '../actions/elements.action';
import * as pageActions from '../actions/page.action';

const isSameElement = (element1, element2) => (
  element1?.id === element2?.id && element1?.pageId === element2?.pageId
);

const isValidLink = (link) => Boolean(link?.id && link?.pageId);

const findTextLink = (chains, prev, next) => {
  const indexes = {
    prevChain: -1,
    prev: -1,
    nextChain: -1,
    next: -1
  };

  chains.forEach((chain, chainIndex) => {
    chain.forEach((element, elementIndex) => {
      if (isSameElement(element, prev)) {
        indexes.prevChain = chainIndex;
        indexes.prev = elementIndex;
      } else if (isSameElement(element, next)) {
        indexes.nextChain = chainIndex;
        indexes.next = elementIndex;
      }
    });
  });

  const prevChain = chains[indexes.prevChain];
  const nextChain = chains[indexes.nextChain];

  return {
    prevChain,
    nextChain,
    indexes
  };
};

const updateTextLinkChain = (chain, element, items = [], deleteCount = 1) => (
  chain
    .splice(element + 1, deleteCount, ...items)
);

const insertChain = (chains, items) => chains.push(items);

const insertNewChain = {
  isApplicable: ({ prevChain, nextChain }) => !prevChain && !nextChain,
  perform: ({ chains, prev, next }) => insertChain(chains, [prev, next])
};

const joinChains = {
  isApplicable: ({ prevChain, prevAndNextChainsAreDifferent, nextIsFirst }) => prevChain && prevAndNextChainsAreDifferent && nextIsFirst,
  perform: ({ chains, prevChain, nextChain, indexes, nextIsFirst }) => {
    if (nextIsFirst) chains.splice(indexes.nextChain, 1);

    const nextAndChildren = nextChain.splice(indexes.next);
    updateTextLinkChain(prevChain, indexes.prev, nextAndChildren, 0);
  }
};

const splitChains = {
  isApplicable: ({ nextChain, nextIsFirst }) =>
    nextChain && !nextIsFirst,
  perform: ({ chains, prevChain, nextChain, indexes, prevIsLast, prev }) => {
    const nextAndChildren = nextChain.splice(indexes.next);

    if (prevChain && prevIsLast) {
      updateTextLinkChain(prevChain, indexes.prev, nextAndChildren);
      return;
    }

    if (prevChain) {
      const prevChildren = prevChain.splice(indexes.prev).slice(1);

      if (prevChain.length <= 1) {
        chains.splice(indexes.prevChain, 1);
      }

      if (prevChildren.length > 1) {
        insertChain(chains, prevChildren);
      }
    }

    insertChain(chains, [prev, ...nextAndChildren]);
  }
};

const appendNextChain = {
  isApplicable: ({ nextChain, prevChain }) => nextChain && !prevChain,
  perform: ({ nextChain, indexes, prev, next }) => {
    updateTextLinkChain(nextChain, indexes.next - 1, [prev, next]);
  }
};

const appendPrevChain = {
  isApplicable: ({ prevAndNextChainsAreDifferent }) => prevAndNextChainsAreDifferent,
  perform: ({ prevChain, nextChain, indexes, next }) => {
    updateTextLinkChain(prevChain, indexes.prev, [next]);
  }
};

const getAddActionToPerform = (actionArgs) => {
  const actions = { insertNewChain, joinChains, splitChains, appendNextChain, appendPrevChain };
  const actionToPerform = Object.values(actions).find(action => action.isApplicable(actionArgs));

  return actionToPerform?.perform;
};

const addTextLink = (state, action) => {
  const getPageIndex = (pageId) => state.pagesOrder.findIndex((id) => `${id}` === `${pageId}`);

  const { prev, next } = action.payload;
  if (isSameElement(prev, next) || !isValidLink(prev) || !isValidLink(next)) return;

  const { linkedTextElements: chains } = state;

  const prevPageIndex = getPageIndex(prev.pageId);
  const nextPageIndex = getPageIndex(next.pageId);
  if (nextPageIndex < prevPageIndex) return;

  const data = { chains, prev, next };
  const textLink = findTextLink(...Object.values(data));
  const { prevChain, nextChain, indexes } = textLink;

  const prevAndNextChainsAreDifferent = indexes.prevChain !== indexes.nextChain;
  const prevIsFirst = indexes.prev === 0;
  const prevIsLast = !!prevChain && indexes.prev === prevChain.length - 1;
  const nextIsFirst = indexes.next === 0;
  const nextIsLast = !!nextChain && indexes.next === nextChain.length - 1;

  const actionArgs = {
    ...data,
    ...textLink,
    prevAndNextChainsAreDifferent,
    prevIsFirst,
    prevIsLast,
    nextIsFirst,
    nextIsLast
  };

  const actionToPerform = getAddActionToPerform(actionArgs);

  if (actionToPerform) actionToPerform(actionArgs);
};

const removeTextLink = (state, action) => {
  const { prev, next } = action.payload;

  if (!prev && !next) return;

  const { linkedTextElements: chains } = state;

  const {
    prevChain,
    indexes,
    nextChain
  } = findTextLink(chains, prev, next);

  const removeLink = (chainExceptLink, beforeLink, afterLink, chainIndex, canSplitChain) => {
    if (!canSplitChain && beforeLink.length > 1) {
      return chains.splice(chainIndex, 1, beforeLink, afterLink);
    }

    if (beforeLink.length === 1 && afterLink.length > 1) {
      return chains.splice(chainIndex, 1, afterLink);
    }

    if (chainExceptLink.length > 1) {
      chains.splice(chainIndex, 1, chainExceptLink);
    } else {
      chains.splice(chainIndex, 1);
    }
  };

  const removeLinkNext = (nextChain, indexes) => {
    const beforeLink = nextChain.slice(0, indexes.next);
    const afterLink = nextChain.slice(indexes.next);
    const chainExceptLink = afterLink;
    const canSplitChain = indexes.next === 1;

    removeLink(chainExceptLink, beforeLink, afterLink, indexes.nextChain, canSplitChain);
  };

  const removeLinkPrev = (prevChain, indexes) => {
    const beforeLink = prevChain.slice(0, indexes.prev + 1);
    const afterLink = prevChain.slice(indexes.prev + 1);
    const chainExceptLink = beforeLink;
    const canSplitChain = indexes.prev >= prevChain?.length - 2;

    removeLink(chainExceptLink, beforeLink, afterLink, indexes.prevChain, canSplitChain);
  };

  if (next && nextChain) removeLinkNext(nextChain, indexes);
  if (prev && prevChain) removeLinkPrev(prevChain, indexes);
};

const setTextLinks = (state, action) => {
  const textLinks = action.payload;
  state.linkedTextElements = textLinks;
};

const removeTextFromLink = (state, { payload }) => {
  const { elementId: id, pageId } = payload;
  const { linkedTextElements: chains } = state;

  state.linkedTextElements = chains
    .map(chain => chain.filter(item => !isSameElement(item, { id, pageId })))
    .filter(chain => chain.length > 1);
};

const removeLinkFromPage = (state, { payload }) => {
  const pageId = Number(payload.id);
  const { linkedTextElements: chains } = state;

  state.linkedTextElements = chains
    .map(chain => chain.filter(item => item.pageId !== pageId))
    .filter(chain => chain.length > 1);
};

const setTextLinksFromMetadata = (state, action) => {
  const { text_overflow: textOverflow } = action.payload;
  if (!textOverflow?.chains) return;

  state.linkedTextElements = textOverflow.chains.map(
    chain => chain.map(
      ({ page_id: pageId, element_id: id }) => ({ id, pageId: Number(pageId) })
    ).filter(isValidLink)
  ).filter(chain => chain.length > 1);
};

export default {
  initialState: {
    linkedTextElements: []
  },
  cases: [
    [editorActions.addTextLink, addTextLink],
    [editorActions.setTextLinks, setTextLinks],
    [editorActions.setTextLinksFromMetadata, setTextLinksFromMetadata],
    [editorActions.removeTextLink, removeTextLink],
    [elementsActions.deleteElement, removeTextFromLink],
    [pageActions.deletePage, removeLinkFromPage]
  ]
};
